import { useViewRDataStore } from "../../store/viewRDataStore";
import { clearViewrVdbGcsPathMemory } from "./viewrVdbGcsPathSession";
import { clearLatestViewRDataDetail } from "../../utils/spaceDeletionSaveRecovery";
import { abortSpaceNavigationTransaction } from "./spaceNavigationCoordinator";
import { getActiveSpaceSwitchTransactionId } from "./spaceSwitchState";
import { rememberEditorExitViewrGcsPath } from "../query/fileCacheSync";
import { clearSpaceAddPersistBarrier } from "./spaceAddPersistBarrier";
import { disposeActiveRevisionAutoSaveCoordinator } from "./revisionAutoSaveRuntime";
import { resetProjectSaveUiSession } from "./projectSaveUiRuntime";
import { cancelPendingEditSnapshotCaptures } from "./completeEditSnapshot";
import { setActiveRevisionProject } from "./revisionAssetRefs";
import {
  beginEditorUndoToastScreenTransitionSession,
  dismissEditorUndoToastForScreenTransition,
  endEditorUndoToastScreenTransitionSession,
} from "./editorUndoToastLifecycle";

export const VIEWR_HOME_NAVIGATION_STARTED_EVENT = "viewr:homeNavigationStarted";

export interface HomeNavigationStartedDetail {
  viewrGcsPath?: string;
}

interface EditorHomeNavigationCleanupOptions {
  source?: string;
  unloadRuntime?: () => Promise<unknown>;
  sendUnityQuitRequest?: () => void;
  clearCaches?: () => Promise<unknown>;
}

export function isEditorHomeNavigationInProgress(): boolean {
  return (
    typeof window !== "undefined" &&
    window.__viewr_homeNavigationInProgress === true
  );
}

export function isEditorHomeNavigationBackgroundCleanupStarted(): boolean {
  return (
    typeof window !== "undefined" &&
    window.__viewr_homeNavigationCleanupStarted === true
  );
}

export function markEditorHomeNavigationStarted(
  viewrGcsPath?: string | null,
): boolean {
  if (typeof window === "undefined") return false;
  const alreadyStarted = window.__viewr_homeNavigationInProgress === true;
  window.__viewr_homeNavigationInProgress = true;
  if (viewrGcsPath) {
    rememberEditorExitViewrGcsPath(viewrGcsPath);
  }
  if (alreadyStarted) return false;
  dismissEditorUndoToastForScreenTransition("editor_unload");
  try {
    window.dispatchEvent(
      new CustomEvent<HomeNavigationStartedDetail>(
        VIEWR_HOME_NAVIGATION_STARTED_EVENT,
        {
          detail: viewrGcsPath ? { viewrGcsPath } : undefined,
        },
      ),
    );
  } catch {
    // Ignore CustomEvent dispatch failures during teardown.
  }
  return true;
}

export function clearEditorHomeNavigationStarted(): void {
  if (typeof window === "undefined") return;
  delete window.__viewr_homeNavigationInProgress;
  delete window.__viewr_homeNavigationCleanupStarted;
}

export function clearPendingAddSpaceNavigationMarkers(): void {
  if (typeof window === "undefined") return;
  delete window.__viewr_pendingAddSpaceTransactionId;
  delete window.__viewr_pendingAddSpaceIndex;
}

export function clearEditorViewRSessionState(): void {
  // 순서가 중요하다. coordinator를 먼저 멈춰야 아래 취소로 실패한 저장이 재시도를
  // 예약하지 않고, 저장 UI를 먼저 비워야 취소 실패 event가 어떤 owned operation과도
  // 맞지 않아 새 화면에 toast·blocker를 남기지 않는다.
  disposeActiveRevisionAutoSaveCoordinator();
  resetProjectSaveUiSession();
  cancelPendingEditSnapshotCaptures();
  // 세션이 끝난 뒤의 Comment staging이 이전 프로젝트에 asset을 등록하지 않게 한다.
  setActiveRevisionProject(null);

  useViewRDataStore.getState().reset();
  clearViewrVdbGcsPathMemory();
  clearLatestViewRDataDetail();
  clearSpaceAddPersistBarrier();
  endEditorUndoToastScreenTransitionSession();

  if (typeof window === "undefined") return;

  delete window.__viewr_currentSpaceIndex;
  delete window.__viewr_uiCurrentSpaceIndex;
  delete window.__viewr_pendingToolSelectSpaceIndex;
  delete window.__viewr_pendingToolSelectSpaceTransactionId;
  delete window.__viewr_skipNextToolSelectSpaceTransactionId;
  delete window.__viewr_skipSpaceSwitchRollbackOnPrepareFailure;
  delete window.__viewr_remountInProgress;
  delete window.__viewr_remountCompletedAt;
  delete window.__viewr_spaceSwitchActive;
  delete window.__viewr_spaceSwitchTransactionId;
  delete window.__viewr_spaceSwitchTransactionType;
  delete window.__viewr_spaceSwitchRestoreMode;
  delete window.__viewr_spaceSwitchStage;
  delete window.__viewr_spaceSwitchPhase;
  delete window.__viewr_spaceSwitchStartedAt;
  delete window.__viewr_spaceSwitchTargetIndex;
  delete window.__viewr_spaceSwitchRunId;
  delete window.__viewr_spaceSwitchCompletedAt;
  clearPendingAddSpaceNavigationMarkers();
  delete window.__viewr_lastSpaceAddAt;
  delete window.__viewr_lastSpaceReorderAt;
  delete window.__viewr_lastSpaceDuplicateAt;
}

/**
 * 에디터에서 Home(또는 동일 세션 종료)으로 나가기 직전에 호출한다.
 * Space 전환(remount) 중이면 Abort로 끊고, 활성 Space 네비게이션 트랜잭션을 abort한다.
 * 이후 `beginEditorHomeNavigationTeardown()`으로 세션 플래그를 정리한다.
 */
export function prepareEditorExitForHomeNavigation(options?: {
  abortInFlightRemount?: () => void;
}): void {
  options?.abortInFlightRemount?.();
  const tx = getActiveSpaceSwitchTransactionId();
  abortSpaceNavigationTransaction({
    transactionId: tx,
    reason: "editor_exit_to_home",
    source: "prepareEditorExitForHomeNavigation",
  });
}

export function beginEditorHomeNavigationTeardown(
  viewrGcsPath?: string | null,
): void {
  markEditorHomeNavigationStarted(viewrGcsPath);
  clearEditorViewRSessionState();
}

export function startEditorHomeNavigationBackgroundCleanup({
  source = "unknown",
  unloadRuntime,
  sendUnityQuitRequest,
  clearCaches,
}: EditorHomeNavigationCleanupOptions): boolean {
  if (typeof window !== "undefined") {
    if (window.__viewr_homeNavigationCleanupStarted === true) {
      return false;
    }
    window.__viewr_homeNavigationCleanupStarted = true;
  }

  try {
    void Promise.resolve(unloadRuntime?.()).catch((err) => {
      if (process.env.NODE_ENV === "development") {
        console.warn(
          `[editorSessionCleanup] Home background unload failed (${source})`,
          err,
        );
      }
    });
  } catch (err) {
    if (process.env.NODE_ENV === "development") {
      console.warn(
        `[editorSessionCleanup] Home background unload start failed (${source})`,
        err,
      );
    }
  }

  try {
    sendUnityQuitRequest?.();
  } catch (err) {
    if (process.env.NODE_ENV === "development") {
      console.warn(
        `[editorSessionCleanup] Home background quit failed (${source})`,
        err,
      );
    }
  }

  try {
    void Promise.resolve(clearCaches?.()).catch((err) => {
      if (process.env.NODE_ENV === "development") {
        console.warn(
          `[editorSessionCleanup] Home background cache cleanup failed (${source})`,
          err,
        );
      }
    });
  } catch (err) {
    if (process.env.NODE_ENV === "development") {
      console.warn(
        `[editorSessionCleanup] Home background cache cleanup start failed (${source})`,
        err,
      );
    }
  }

  return true;
}

export function beginEditorSession(): void {
  clearEditorHomeNavigationStarted();
  beginEditorUndoToastScreenTransitionSession();
}
